const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DEFAULT_PRE_APPROVAL_TIME_ZONE = 'America/Los_Angeles';

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    second: '2-digit'
  }).formatToParts(date);

  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  const hour = Number(values.hour);
  const normalizedHour = hour === 24 ? 0 : hour;
  const asUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    normalizedHour,
    Number(values.minute),
    Number(values.second)
  );

  return asUtc - date.getTime();
}

function dateOnlyEndOfDayUtc(dateOnly: string, timeZone: string): Date | null {
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (!year || !month || !day) {
    return null;
  }

  let nextDayMidnightUtcMs = Date.UTC(year, month - 1, day + 1, 0, 0, 0, 0);
  for (let i = 0; i < 3; i += 1) {
    const offsetMs = getTimeZoneOffsetMs(new Date(nextDayMidnightUtcMs), timeZone);
    const adjusted = Date.UTC(year, month - 1, day + 1, 0, 0, 0, 0) - offsetMs;
    if (adjusted === nextDayMidnightUtcMs) {
      break;
    }
    nextDayMidnightUtcMs = adjusted;
  }

  return new Date(nextDayMidnightUtcMs - 1);
}

export function resolveCanonicalPreApprovalExpiresAt(
  expiresAt?: string | null,
  legacyExpirationDate?: string | null
): string | undefined {
  return expiresAt || legacyExpirationDate || undefined;
}

export function getPreApprovalExpirationInstant(
  expiresAt?: string | null,
  timeZone: string = DEFAULT_PRE_APPROVAL_TIME_ZONE
): string | undefined {
  if (!expiresAt) {
    return undefined;
  }

  if (DATE_ONLY_PATTERN.test(expiresAt)) {
    return dateOnlyEndOfDayUtc(expiresAt, timeZone)?.toISOString();
  }

  const parsed = new Date(expiresAt);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

export function isPreApprovalExpired(
  expiresAt?: string | null,
  now: Date = new Date(),
  timeZone: string = DEFAULT_PRE_APPROVAL_TIME_ZONE
): boolean {
  const expirationInstant = getPreApprovalExpirationInstant(expiresAt, timeZone);
  return expirationInstant ? new Date(expirationInstant).getTime() < now.getTime() : false;
}

export function formatPreApprovalExpirationDate(
  expiresAt?: string | null,
  timeZone: string = DEFAULT_PRE_APPROVAL_TIME_ZONE
): string | undefined {
  const expirationInstant = getPreApprovalExpirationInstant(expiresAt, timeZone);
  if (!expirationInstant) {
    return undefined;
  }

  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(new Date(expirationInstant));
}

export function formatPreApprovalExpirationDateInput(
  expiresAt?: string | null,
  timeZone: string = DEFAULT_PRE_APPROVAL_TIME_ZONE
): string | undefined {
  const expirationInstant = getPreApprovalExpirationInstant(expiresAt, timeZone);
  if (!expirationInstant) {
    return undefined;
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date(expirationInstant));

  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}
